import React, { useState } from "react";
import {
  Alert,
  Button,
  CopyToClipboard,
  FormField,
  Input,
  SpaceBetween,
} from "@cloudscape-design/components";
import { ApiHelper } from "../../Helpers/apiHelper";
import { cloudcallIdValidator } from "../../Helpers/validator";
import CustomerIdSelector from "../CustomerIdSelector";
import AuthConnectorProps from "./ConnectorTypes";

const connectorName = "TrackerRms";

const GetTrackerAuthUrl = ({
  setNotifications,
  clearNotifications,
  connectorId,
}: AuthConnectorProps) => {
  const [apiHelper] = useState(ApiHelper());
  const [isLoading, setIsLoading] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [clientID, setClientID] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [crmUserApiUrl, setCrmUserApiUrl] = useState("");
  const [applicationKey, setApplicationKey] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [clientConnectorId, setClientConnectorId] = useState("");

  const getButtonIsDisabled = () => {
    return (
      customerId.length === 0 ||
      !cloudcallIdValidator(customerId) ||
      !clientID.length ||
      !webhookUrl ||
      !crmUserApiUrl ||
      !applicationKey ||
      !authToken
    );
  };

  const getUrl = async () => {
    setIsLoading(true);
    clearNotifications();
    const existingConnectors = await apiHelper
      .makeSignedRequest(`api/clientConnectors?customerId=${customerId}`)
      .then((res) => {
        return res?.data.filter((connector: any) => {
          return (
            connector?.clientConfiguration?.integrationName === connectorName
          );
        });
      });
    if (existingConnectors.length > 1) {
      let error = (
        <Alert
          statusIconAriaLabel="Info"
          dismissible
          //@ts-ignore
          onDismiss={clearNotifications}
          //@ts-ignore
          type="error"
          header="Error"
          key={"errorFetching"}
        >
          Found multiple client connectors, please provide Client Connector Id
        </Alert>
      );
      setNotifications(error);
      setIsLoading(false);
      return;
    }
    let targetUrl = `api/connectors/v1/oauth/login?connectorName=${connectorName}&customerId=${customerId}&connectorId=${connectorId}&clientId=${clientID}&applicationKey=${applicationKey}&authToken=${authToken}&webhookUrl=${webhookUrl}&crmUserApiUrl=${crmUserApiUrl}`;
    if (clientConnectorId) {
      targetUrl = `${targetUrl}&clientConnectorId=${clientConnectorId}`;
    }
    const customerMessage = `Please send the following url to the customer to create a new ${connectorName} integration:`;
    apiHelper
      .makeSignedRequest(targetUrl)
      .then((res) => {
        let error = (
          <Alert
            statusIconAriaLabel="Info"
            dismissible
            //@ts-ignore
            onDismiss={clearNotifications}
            //@ts-ignore
            type="error"
            header="Error"
            key={"errorFetching"}
          >
            An error occurred fetching the url, please check the values entered
            or refresh and try again
          </Alert>
        );
        if (res && res.data.redirectUrl) {
          error = (
            <Alert
              statusIconAriaLabel="Info"
              dismissible
              //@ts-ignore
              onDismiss={clearNotifications}
              //@ts-ignore
              type="success"
              header="Request Successful"
              key={"success"}
            >
              <SpaceBetween size={"xs"}>
                <div key={"title"}>{customerMessage}</div>
                <CopyToClipboard
                  copyButtonText="Copy"
                  copyErrorText="URL failed to copy"
                  copySuccessText="URL copied"
                  variant={"inline"}
                  //@ts-ignore
                  textToCopy={res.data.redirectUrl}
                  key={"copy"}
                />
              </SpaceBetween>
            </Alert>
          );
        }
        setNotifications(error);
      })
      .catch((e) => {
        let message =
          "An error occurred fetching the url, please check the values entered or refresh and try again";
        let header = "Invalid Request";
        setNotifications(
          <Alert
            statusIconAriaLabel="Error"
            dismissible
            //@ts-ignore
            onDismiss={clearNotifications}
            //@ts-ignore
            type="error"
            header={header}
            key={"errorFetching"}
          >
            {message}
          </Alert>
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <SpaceBetween size={"m"}>
        <FormField label={<>Customer ID <span style={{ color: "red" }}>*</span></>}>
          <CustomerIdSelector
            initialValue={customerId}
            onChange={setCustomerId}
            invalid={!cloudcallIdValidator(customerId)}
          />
        </FormField>
        <FormField label={<>Tracker Client ID <span style={{ color: "red" }}>*</span></>}>
          <Input
            value={clientID}
            onChange={({ detail }) => {
              setClientID(detail.value);
            }}
          />
        </FormField>
        <FormField label={<>Tracker Client Secret <span style={{ color: "red" }}>*</span></>}>
          <Input
            value={applicationKey}
            onChange={({ detail }) => {
              setApplicationKey(detail.value);
            }}
          />
        </FormField>
        <FormField label={<>Auth Token <span style={{ color: "red" }}>*</span></>}>
          <Input
            value={authToken}
            onChange={({ detail }) => {
              setAuthToken(detail.value);
            }}
          />
        </FormField>
        <FormField label={<>Webhook URL <span style={{ color: "red" }}>*</span></>}>
          <Input
            value={webhookUrl}
            onChange={({ detail }) => {
              setWebhookUrl(detail.value);
            }}
          />
        </FormField>
        <FormField label={<>CRM User API URL <span style={{ color: "red" }}>*</span></>}>
          <Input
            value={crmUserApiUrl}
            onChange={({ detail }) => {
              setCrmUserApiUrl(detail.value);
            }}
          />
        </FormField>
        <FormField label="Client Connector Id">
          <Input
            value={clientConnectorId}
            onChange={({ detail }) => {
              setClientConnectorId(detail.value);
            }}
          />
        </FormField>
        <Button
          variant={"primary"}
          disabled={getButtonIsDisabled()}
          onClick={() => getUrl()}
          loading={isLoading}
        >
          Submit Authentication Credentials
        </Button>
      </SpaceBetween>
    </>
  );
};

export default GetTrackerAuthUrl;
